import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { getStripeConnectStatus } from '../../lib/api';
import { Loader2, CheckCircle, AlertCircle, RefreshCw } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

export default function StripeReturnPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, loading: authLoading, isAuthenticated } = useAuth();
  
  const [status, setStatus] = useState('loading'); // loading, success, incomplete, error
  const [message, setMessage] = useState('');
  const isRefresh = searchParams.get('refresh') === '1';

  useEffect(() => {
    // Wait for auth to hydrate
    if (authLoading) {
      return;
    }

    // If not authenticated after auth loads, redirect to login with return URL
    if (!isAuthenticated) {
      navigate('/login?next=/app/stripe/return', { replace: true });
      return;
    }

    // If authenticated but not a host, redirect to guest dashboard
    if (user && user.role !== 'host' && user.role !== 'admin') {
      navigate('/app', { replace: true });
      return;
    }

    // Check Stripe status
    checkStripeStatus();
  }, [authLoading, isAuthenticated, user, navigate]);

  const checkStripeStatus = async () => {
    try {
      const response = await getStripeConnectStatus();
      const data = response.data;

      if (data.status === 'verified') {
        setStatus('success');
        setMessage('Your Stripe account is now connected and verified!');
        toast.success('Stripe connected successfully!');
        
        // Redirect to host dashboard after a short delay
        setTimeout(() => {
          navigate('/app/host', { replace: true });
        }, 2000);
      } else if (data.status === 'pending') {
        if (data.details_submitted) {
          setStatus('success');
          setMessage('Your Stripe account is connected. Verification is in progress.');
          toast.success('Stripe setup complete! Verification pending.');
          
          setTimeout(() => {
            navigate('/app/host', { replace: true });
          }, 2000);
        } else {
          setStatus('incomplete');
          setMessage('Stripe setup was not completed. Please try again.');
        }
      } else if (data.status === 'restricted') {
        setStatus('incomplete');
        setMessage('Your Stripe account needs additional information.');
      } else {
        setStatus('incomplete');
        setMessage(isRefresh 
          ? 'The Stripe setup link expired. Please try again.'
          : 'Stripe setup was not completed. Please try again.'
        );
      }
    } catch (error) {
      console.error('Failed to check Stripe status:', error);
      setStatus('error');
      setMessage('Failed to verify Stripe status. Please try again.');
    }
  };

  const handleRetry = () => {
    navigate('/app/host', { replace: true });
  };

  // Show loading while auth is hydrating
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
          <p className="text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
      <div className="w-full max-w-md bg-white rounded-xl shadow-lg p-8 text-center">
        {status === 'loading' && (
          <>
            <Loader2 className="w-16 h-16 animate-spin text-primary mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-slate-900 mb-2">
              Verifying Stripe Setup...
            </h1>
            <p className="text-slate-500">
              Please wait while we confirm your Stripe account status.
            </p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2">
              Stripe Connected!
            </h1>
            <p className="text-slate-500 mb-4">
              {message}
            </p>
            <p className="text-sm text-slate-400">
              Redirecting to your dashboard...
            </p>
          </>
        )}

        {status === 'incomplete' && (
          <>
            <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-amber-600" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2">
              Setup Incomplete
            </h1>
            <p className="text-slate-500 mb-6">
              {message}
            </p>
            <Button onClick={handleRetry} className="w-full">
              <RefreshCw className="w-4 h-4 mr-2" />
              Return to Dashboard
            </Button>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-red-600" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2">
              Something Went Wrong
            </h1>
            <p className="text-slate-500 mb-6">
              {message}
            </p>
            <Button onClick={handleRetry} className="w-full">
              <RefreshCw className="w-4 h-4 mr-2" />
              Return to Dashboard
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
